<template>
  <div class="vue-country-select" :class="{ disabled: disabled }">
    <!-- {{ countries }} -->
    <div class="dropdown" @click="toggleDropdown" v-click-out-side="onClickOutside" :class="{ open: open }"
      @keydown="keyboardNav" tabindex="0" @keydown.esc="reset">
      <span class="current">
        <span v-if="enabledCountryCode" class="country-code">{{ activeCountry.dialCode }}</span>
        <div v-if="enabledFlags" class="vti__flag" :class="activeCountry.iso2.toLowerCase()"></div>
        <span class="dropdown-arrow">{{ open ? "▲" : "▼" }}</span>
      </span>
      <ul v-show="open" ref="list" class="dropdown-list">
        <li class="dropdown-item" v-for="(country, index) in sortedCountries"
          :key="country.iso2 + (country.preferred ? '-preferred' : '')" @click="choose(country)"
          :class="getItemClass(index, country.iso2)" @mousemove="selectedIndex = index">
          <div class="vti__flag" v-if="enabledFlags" :class="country.iso2.toLowerCase()"></div>
          <strong>{{ country.name }}</strong>
          <span v-if="dropdownOptions && !dropdownOptions.disabledDialCode">{{ country.dialCode }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import getCountry from "../utils/defaultCountry";
import clickOutSide from "@mahdikhashan/vue3-click-outside";

export default {
  name: "vue-country-code",
  props: {
    disabledFetchingCountry: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    disabledFormatting: {
      type: Boolean,
      default: false
    },
    defaultCountry: {
      type: String,
      default: ""
    },
    enabledCountryCode: {
      type: Boolean,
      default: true
    },
    enabledFlags: {
      type: Boolean,
      default: true
    },
    preferredCountries: {
      type: Array,
      default: () => []
    },
    onlyCountries: {
      type: Array,
      default: () => []
    },
    ignoredCountries: {
      type: Array,
      default: () => []
    },
    dropdownOptions: {
      type: Object,
      default: () => ({})
    },
    selectedCountryCode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      activeCountry: { iso2: "" },
      open: false,
      manualTrigger: false,
      selectedIndex: null,
      countries: [] // Store fetched countries
    };
  },
  computed: {
    filteredCountries() {
      if (this.onlyCountries.length) {
        return this.getCountries(this.onlyCountries);
      }

      if (this.ignoredCountries.length) {
        return this.countries.filter(
          ({ iso2 }) =>
            !this.ignoredCountries.includes(iso2.toUpperCase()) &&
            !this.ignoredCountries.includes(iso2.toLowerCase())
        );
      }

      return this.countries;
    },
    sortedCountries() {
      const preferredCountries = this.getCountries(this.preferredCountries).map(country => ({
        ...country,
        preferred: true
      }));

      // Combine and remove duplicates again
      return [...preferredCountries, ...this.filteredCountries].filter(
        (country, index, self) => index === self.findIndex(c => c.iso2 === country.iso2)
      );
    }
  },
  watch: {
    defaultCountry(newVal) {
      const defaultCountry = this.findCountry(newVal);
      this.choose(defaultCountry);
    }
  },
  mounted() {
    this.initializeCountry();
    this.$emit("onSelect", this.activeCountry);
  },
  methods: {
    async initializeCountry() {
      try {
        // Fetch countries from API
        const response = await fetch("https://backend.nadyalloughat.com/api/check-country-digits");
        const data = await response.json();

        // console.log('data country', this.countries);

        // Map API response to component's country format
        const processedCountries = data.map(country => ({
          name: country.name,
          iso2: country.code.toLowerCase(),
          dialCode: country.dial_code
        }));

        // Remove duplicates by `iso2` (or another unique property)
        this.countries = processedCountries.filter((country, index, self) =>
          index === self.findIndex(c => c.iso2 === country.iso2)
        );


        this.activeCountry =
          this.findCountry(this.defaultCountry) ||
          this.findCountry(this.preferredCountries[0]) ||
          this.countries[0];

        if (!this.disabledFetchingCountry) {
          const countryFromIP = await getCountry();
          this.choose(this.findCountry(countryFromIP) || this.activeCountry);
        }
      } catch (error) {
        console.error("Failed to fetch countries:", error);
      }
    },
    getCountries(list = []) {
      return list
        .map(countryCode => this.findCountry(countryCode))
        .filter(Boolean);
    },
    findCountry(iso = "") {
      return this.countries.find(country => country.iso2 === iso.toLowerCase());
    },
    choose(country) {
      this.activeCountry = country;
      this.$emit("onSelect", this.activeCountry);
    },
    getItemClass(index, iso2) {
      const highlighted = this.selectedIndex === index;
      const lastPreferred = index === this.preferredCountries.length - 1;
      const preferred = !!~this.preferredCountries
        .map(c => c.toUpperCase())
        .indexOf(iso2);
      return {
        highlighted,
        "last-preferred": lastPreferred,
        preferred
      };
    },
    toggleDropdown() {
      if (this.disabled) {
        return;
      }
      this.open = !this.open;
    },
    onClickOutside() {
      if (this.manualTrigger) {
        this.manualTrigger = false;
        return;
      }
      this.open = false;
    },
    keyboardNav(e) {
      if (e.keyCode === 40) {
        // down arrow
        this.open = true;
        if (this.selectedIndex === null) {
          this.selectedIndex = 0;
        } else {
          this.selectedIndex = Math.min(
            this.sortedCountries.length - 1,
            this.selectedIndex + 1
          );
        }
        let selEle = this.$refs.list.children[this.selectedIndex];
        if (
          selEle.offsetTop + selEle.clientHeight >
          this.$refs.list.scrollTop + this.$refs.list.clientHeight
        )
          this.$refs.list.scrollTop =
            selEle.offsetTop -
            this.$refs.list.clientHeight +
            selEle.clientHeight;
      } else if (e.keyCode === 38) {
        // up arrow
        this.open = true;
        if (this.selectedIndex === null) {
          this.selectedIndex = this.sortedCountries.length - 1;
        } else {
          this.selectedIndex = Math.max(0, this.selectedIndex - 1);
        }
        let selEle = this.$refs.list.children[this.selectedIndex];
        if (selEle.offsetTop < this.$refs.list.scrollTop)
          this.$refs.list.scrollTop = selEle.offsetTop;
      } else if (e.keyCode === 13) {
        // enter key
        if (this.selectedIndex !== null) {
          this.choose(this.sortedCountries[this.selectedIndex]);
        }
        this.open = !this.open;
      } else {
        // typing a country's name
        this.typeToFindInput += e.key;
        clearTimeout(this.typeToFindTimer);
        this.typeToFindTimer = setTimeout(() => {
          this.typeToFindInput = "";
        }, 700);
        // don't include preferred countries so we jump to the right place in the alphabet
        let typedCountryI = this.sortedCountries
          .slice(this.preferredCountries.length)
          .findIndex(c =>
            c.name.toLowerCase().startsWith(this.typeToFindInput)
          );
        if (~typedCountryI) {
          this.selectedIndex = this.preferredCountries.length + typedCountryI;
          let selEle = this.$refs.list.children[this.selectedIndex];
          if (
            selEle.offsetTop < this.$refs.list.scrollTop ||
            selEle.offsetTop + selEle.clientHeight >
            this.$refs.list.scrollTop + this.$refs.list.clientHeight
          ) {
            this.$refs.list.scrollTop =
              selEle.offsetTop - this.$refs.list.clientHeight / 2;
          }
        }
      }
    },
    reset() {
      this.selectedIndex = this.sortedCountries
        .map(c => c.iso2)
        .indexOf(this.activeCountry.iso2);
      this.open = false;
    }
  },
  directives: {
    clickOutSide,
    // Click-outside from BosNaufal: https://github.com/BosNaufal/vue-click-outside
    "click-outside": {
      bind: function (el, binding, vNode) {
        // Provided expression must evaluate to a function.
        if (typeof binding.value !== "function") {
          var compName = vNode.context.name;
          var warn =
            "[Vue-click-outside:] provided expression " +
            binding.expression +
            " is not a function, but has to be";
          if (compName) {
            warn += "Found in component " + compName;
          }
          console.warn(warn);
        }
        // Define Handler and cache it on the element
        var bubble = binding.modifiers.bubble;
        var handler = function (e) {
          if (bubble || (!el.contains(e.target) && el !== e.target)) {
            binding.value(e);
          }
        };
        el.__vueClickOutside__ = handler;
        // add Event Listeners
        document.addEventListener("click", handler);
      },
      unbind: function (el) {
        // Remove Event Listeners
        document.removeEventListener("click", el.__vueClickOutside__);
        el.__vueClickOutside__ = null;
      }
    }
  }

};


</script>



<!-- <style src="../styles/sprite.css"></style> -->
<style>
/* TODO: Find the right way to resolve alias in style block */
/* @import url("~@/assets/sprite.css"); */
.vue-country-select {
  border-radius: 0;
  display: inline-block;
  border: none;
  text-align: left;
}

.vue-country-select:focus-within {
  /* box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075),
    0 0 8px rgba(102, 175, 233, 0.6);
  border-color: #66afe9; */
}

.vue-country-select .dropdown {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 0.5em;
  position: relative;
  cursor: pointer;
}

.vue-country-select .dropdown.open {
  background-color: #f3f3f3;
}

.vue-country-select .dropdown:hover {
  background-color: #f3f3f3;
}

.vue-country-select .dropdown-list {
  z-index: 1;
  padding: 0;
  margin: 0;
  text-align: left;
  list-style: none;
  max-height: 200px;
  overflow-y: scroll;
  position: absolute;
  top: 100%;
  left: -2px;
  background-color: #fff;
  border: 1px solid #ccc;
  width: 320px;
}

.vue-country-select .dropdown-item {
  cursor: pointer;
  padding: 4px 15px;
}

.vue-country-select .dropdown-item .iti-flag {
  display: inline-block;
  margin-right: 5px;
  margin-left: 5px;
}

.vue-country-select .dropdown-item.highlighted {
  background-color: #f3f3f3;
}

.vue-country-select .dropdown-item.last-preferred {
  border-bottom: 1px solid #cacaca;
}

.vue-country-select .dropdown-arrow {
  transform: scaleY(0.5);
  display: inline-block;
  color: #666;
}

.vue-country-select .current {
  font-size: 0.8em;
  display: flex;
  align-items: center;
}

.vue-country-select .country-code {
  color: #3B3786;
  font-family: Poppins;
  font-weight: 600;
  font-size: 18px;
}

.vue-country-select.disabled .current,
.vue-country-select.disabled .dropdown {
  cursor: no-drop;
}

@media screen and (max-width:766px) {
  .vue-country-select .dropdown-list {
    left: -8px;
    right: initial;
    width: 290px;
  }

  .vue-country-select .country-code {
    font-size: 14px;
  }

  .vue-country-select .dropdown-item {
    font-size: 14px;
    white-space: initial;
  }
}
</style>
